"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, CircleCheck as CheckCircle2, TrendingUp, Sparkles, ShieldCheck, Loader as Loader2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const benefits = [{
  id: 1,
  title: "Weekly OTA Algorithm Updates",
  description: "Actionable tips to maximize visibility on Booking.com & Expedia."
}, {
  id: 2,
  title: "Direct Booking Strategies",
  description: "Reduce commission costs and boost direct revenue streams."
}, {
  id: 3,
  title: "Hospitality Tech Insights",
  description: "Honest audits of RMS, PMS, and channel management tools."
}];
function NewsletterFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl bg-card p-6 border border-primary/30 shadow-md text-center space-y-4">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/20 text-primary mx-auto">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-bold text-card-foreground">{"Subscription Confirmed!"}</h3>
        <p className="text-muted-foreground text-sm max-w-md mx-auto">{successMessage}</p>
        <Button variant="outline" onClick={resetForm} className="mt-2">
          {"Subscribe Another Email"}
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
        <div className="sm:col-span-8">
          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="sr-only">{"Email Address"}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input type="email" placeholder="Enter your work email address" className="pl-10 h-12 bg-background text-foreground border-border placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:border-primary rounded-lg" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="sm:col-span-4">
          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-lg shadow-sm">
            {isSubmitting ? <>
                <Loader2 className="animate-spin size-4 mr-2" />
                {"Joining..."}
              </> : <>
                <span>{"Get Free Insights"}</span>
              </>}
          </Button>
        </div>
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-sm">
          {submitError}
        </div>}

      <div className="flex items-center gap-2 text-xs text-secondary-foreground/70 justify-center sm:justify-start pt-1">
        <ShieldCheck className="size-4 text-primary shrink-0" />
        <span>{"Zero spam. Unsubscribe anytime with one click."}</span>
      </div>
    </Form>;
}
export default function Newsletter() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="newsletterinsightssignup" data-nav="light" className="py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-secondary text-secondary-foreground rounded-3xl p-8 md:p-14 shadow-2xl border border-border/20 relative overflow-hidden">
          {}
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/15 text-primary border border-primary/30 text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="size-3.5" />
                <span>{"Exclusive Hotelier Intelligence"}</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-secondary-foreground tracking-tight leading-tight">
                {"Stay Ahead of Hotel Revenue & OTA Trends"}
              </h2>

              <p className="text-secondary-foreground/80 text-base md:text-lg leading-relaxed max-w-2xl">
                {"Join 1,200+ hotel owners, general managers, and property investors receiving Sunil Singh’s weekly data-driven revenue management strategies."}
              </p>

              {}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {benefits.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col gap-1.5 bg-secondary-foreground/5 p-3.5 rounded-xl border border-secondary-foreground/10">
                    <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                      <TrendingUp className="size-4 shrink-0" />
                      <span>{item.title}</span>
                    </div>
                    <p className="text-xs text-secondary-foreground/70 leading-normal">
                      {item.description}
                    </p>
                  </div>)}
              </div>
            </motion.div>

            {}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="lg:col-span-5 bg-card/10 backdrop-blur-md p-6 sm:p-8 rounded-2xl border border-secondary-foreground/15 shadow-inner">
              <div className="mb-6 space-y-2">
                <h3 className="text-xl font-bold text-secondary-foreground">
                  {"Subscribe to Insights"}
                </h3>
                <p className="text-sm text-secondary-foreground/80">
                  {"Get practical strategies delivered straight to your inbox every Tuesday."}
                </p>
              </div>

              <FormProvider formId="newsletter_signup_insights" sectionName="newsletter-insights-signup" defaultValues={{
              email: ""
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              }
            }} submitText="Get Free Insights" successMessage="Thank you for subscribing! Check your inbox for our latest hotel revenue strategies.">
                <NewsletterFormContent />
              </FormProvider>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}